import { useNavigate } from 'react-router-dom';
import { ChevronsLeft, ChevronsRight, LogOut, Menu, Search } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { useUiStore } from '@/stores/ui.store';
import { useBranches } from '@/queries/users.queries';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CampanaDeAvisos } from '@/components/layout/CampanaDeAvisos';
import { initials } from '@/lib/utils';

export function Topbar() {
  const nav = useNavigate();
  const user = useAuthStore((s) => s.user);
  const tenant = useAuthStore((s) => s.tenant);
  const logout = useAuthStore((s) => s.logout);
  const {
    sidebarCollapsed, toggleSidebar,
    activeBranchId, setActiveBranch,
    setMobileDrawer,
  } = useUiStore();
  const branches = useBranches();

  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-border bg-background/80 px-3 backdrop-blur sm:h-16 sm:gap-4 sm:px-4">
      {/* Mobile/tablet: hamburger that opens the navigation drawer */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setMobileDrawer(true)}
        className="lg:hidden"
        aria-label="Abrir menú"
      >
        <Menu className="h-5 w-5" />
      </Button>

      {/* Desktop: collapse sidebar toggle */}
      <Button variant="ghost" size="icon" onClick={toggleSidebar} className="hidden lg:flex">
        {sidebarCollapsed ? <ChevronsRight className="h-4 w-4" /> : <ChevronsLeft className="h-4 w-4" />}
      </Button>

      {/* Search — hidden on mobile to save space */}
      <div className="hidden flex-1 max-w-md md:block">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Buscar clientes, mascotas, citas… (⌘K)" className="pl-9" />
        </div>
      </div>

      <div className="ml-auto flex items-center gap-1.5 sm:gap-3">
        {/* Branch selector — hide on small screens (lives in the drawer instead) */}
        {branches.data && branches.data.length > 1 && (
          <Select
            value={activeBranchId ?? 'all'}
            onValueChange={(v) => setActiveBranch(v === 'all' ? null : v)}
          >
            <SelectTrigger className="hidden md:flex w-[180px]">
              <SelectValue placeholder="Sucursal" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las sucursales</SelectItem>
              {branches.data.map((b) => (
                <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <CampanaDeAvisos />

        <div className="flex items-center gap-1 sm:gap-2">
          <Avatar className="h-8 w-8 sm:h-9 sm:w-9">
            <AvatarImage src={user?.avatarUrl ?? undefined} />
            <AvatarFallback>{initials(user?.firstName ?? '', user?.lastName ?? '')}</AvatarFallback>
          </Avatar>
          {/* Name+tenant — hide on small screens to keep header clean */}
          <div className="hidden text-right md:block">
            <p className="text-sm font-medium leading-tight">
              {user?.firstName} {user?.lastName}
            </p>
            <p className="text-xs text-muted-foreground">{tenant?.name}</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={async () => { await logout(); nav('/login'); }}
            aria-label="Salir"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </header>
  );
}
